<script setup>
// BonBoZ: banner "điện thoại của bạn không nhận được thông báo".
//
// Hiện hay không và nội dung gì do `usePushBanner` (helper/pushBanner.js)
// quyết, gồm cả thứ tự so với dải nhắc cũ. Không có nút "Để sau": banner tự
// tắt khi hết vấn đề.
//
// Trên điện thoại đang bị chặn, banner hiện NGAY theo quyền đọc tại máy, không
// chờ server; server vẫn được hỏi lại ở nền và quyết khi quyền đã cấp.
//
// Nút sửa chỉ có trên điện thoại: bật trên laptop không giúp điện thoại nhận
// được, nên laptop chỉ nhắc mở BonBoZ trên điện thoại, kèm nút phụ "Bật trên
// máy tính này" khi trình duyệt này chưa bật.
//
// App đang tự kết nối lại cho điện thoại này thì chỉ báo "Đang kết nối lại
// thông báo…", không nút: bấm lúc đó chỉ chen vào lượt đang chạy.
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { getUnixTime, parseISO } from 'date-fns';
import { messageStamp } from 'shared/helpers/timeHelper';
import { detectOS, OS } from 'shared/helpers/platform';
import { getDeviceType } from 'dashboard/helper/deviceIdentity';
import {
  isIOSBrowserTab,
  requestPushPermissions,
  setPushOptOut,
} from 'dashboard/helper/pushHelper';
import { onPageVisible } from 'dashboard/helper/pageVisibility';
import { pushHealth, refreshPushHealth } from 'dashboard/helper/pushHealth';
import { refreshDevicePermission } from 'dashboard/helper/pushDeviceState';
import { syncAfterPushChange } from 'dashboard/helper/pushRecovery';
import { PUSH_BANNER, usePushBanner } from 'dashboard/helper/pushBanner';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();

const isPhone = getDeviceType() === 'phone';
// iPhone trong tab Safari không bật được gì, phải cài app trước.
const needsIOSInstall =
  isPhone && !('Notification' in window) && isIOSBrowserTab();

const MESSAGE_KEYS = {
  lost: 'BONBOZ.PUSH_HEALTH.LOST',
  disabled: 'BONBOZ.PUSH_HEALTH.DISABLED',
  never_registered: 'BONBOZ.PUSH_HEALTH.NEVER_REGISTERED',
  reconnecting: 'BONBOZ.QUYEN_THONG_BAO.DANG_KET_NOI_LAI',
};
const BUTTON_KEYS = {
  lost: 'BONBOZ.PUSH_HEALTH.REENABLE',
  disabled: 'BONBOZ.PUSH_HEALTH.REENABLE',
  never_registered: 'BONBOZ.PUSH_HEALTH.ENABLE',
};
const BLOCKED_KEYS = {
  [OS.IOS]: 'BONBOZ.PUSH_HEALTH.BLOCKED_IOS',
  [OS.ANDROID]: 'BONBOZ.PUSH_HEALTH.BLOCKED_ANDROID',
};

// Đọc quyền ngay lúc dựng, để lần vẽ đầu đã đúng.
refreshDevicePermission();
const banner = usePushBanner();
const status = computed(() => banner.value.status);
const isFixableHere = computed(() => !!BUTTON_KEYS[status.value]);
const isReconnecting = computed(() => status.value === 'reconnecting');

const lastReceivedText = () => {
  const iso = pushHealth.value?.last_received_at;
  return iso
    ? messageStamp(getUnixTime(parseISO(iso)), 'HH:mm dd/MM/yyyy')
    : '';
};

const message = computed(() => {
  if (status.value === 'blocked') {
    return t(BLOCKED_KEYS[detectOS()] || 'BONBOZ.PUSH_HEALTH.BLOCKED_OTHER');
  }
  if (status.value === 'unknown') {
    const time = lastReceivedText();
    return time
      ? t('BONBOZ.PUSH_HEALTH.UNKNOWN', { time })
      : t('BONBOZ.PUSH_HEALTH.UNKNOWN_NO_TIME');
  }
  return MESSAGE_KEYS[status.value] ? t(MESSAGE_KEYS[status.value]) : '';
});

// Chỉ hiện với trạng thái đã biết, kẻo server thêm trạng thái mới thì banner rỗng.
const isVisible = computed(
  () => banner.value.banner === PUSH_BANNER.HEALTH && !!message.value
);

const showButton = computed(
  () => isFixableHere.value && isPhone && !needsIOSInstall
);
const showEnableHere = computed(() => banner.value.enableHere);

const hint = computed(() => {
  if (!isFixableHere.value) return '';
  if (!isPhone) return t('BONBOZ.PUSH_HEALTH.OPEN_ON_PHONE');
  return needsIOSInstall ? t('BONBOZ.QUYEN_THONG_BAO.CAN_CAI_APP_IOS') : '';
});

const isEnabling = ref(false);

// Cú bấm thật nên iOS cho `subscribe()`. Lỗi (nếu có) do `pushHelper` tự báo.
// Xong là cập nhật banner, Cài đặt và server ngay.
const runEnable = async ({ clearOptOut = false } = {}) => {
  if (isEnabling.value) return;
  isEnabling.value = true;
  try {
    if (clearOptOut) setPushOptOut(false);
    await requestPushPermissions();
    await syncAfterPushChange();
  } finally {
    isEnabling.value = false;
  }
};

const enable = () => runEnable({ clearOptOut: status.value === 'disabled' });
const enableHere = () => runEnable();

let stopVisibilityListener = null;
onMounted(() => {
  refreshPushHealth();
  stopVisibilityListener = onPageVisible(refreshDevicePermission);
});
onUnmounted(() => stopVisibilityListener?.());
</script>

<template>
  <div
    v-show="isVisible"
    class="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-1.5 text-sm bg-n-amber-3 text-n-amber-12"
    role="alert"
    data-testid="push-health-banner"
  >
    <Spinner
      v-if="isReconnecting"
      :size="16"
      class="shrink-0 text-n-amber-11"
      aria-hidden="true"
      data-testid="push-health-reconnecting"
    />
    <span
      v-else
      class="i-lucide-bell-off shrink-0 size-4 text-n-amber-11"
      aria-hidden="true"
    />
    <span data-testid="push-health-message">{{ message }}</span>
    <span v-if="hint" class="font-medium" data-testid="push-health-hint">
      {{ hint }}
    </span>
    <button
      v-if="showButton"
      class="px-2.5 py-1 font-medium rounded-lg bg-n-brand text-white hover:opacity-90 disabled:opacity-60"
      :disabled="isEnabling"
      data-testid="push-health-fix"
      @click="enable"
    >
      {{ $t(BUTTON_KEYS[status]) }}
    </button>
    <button
      v-if="showEnableHere"
      class="px-2.5 py-1 font-medium rounded-lg border border-solid border-n-amber-7 hover:bg-n-amber-4 disabled:opacity-60"
      :disabled="isEnabling"
      data-testid="push-health-enable-here"
      @click="enableHere"
    >
      {{ $t('BONBOZ.PUSH_HEALTH.ENABLE_HERE') }}
    </button>
  </div>
</template>
